import { useLatest } from '@qfa/react-components';
import { BaseRow } from 'components/Table/types';
import React, { ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

import ColumnsContext, { ColumnConfig } from '../../contexts/ColumnsContext';
import { ColumnProps } from './types';

const defaultRender = (value: unknown) => value as React.ReactNode;
const defaultWrap = (rendered: React.ReactNode, _value: unknown, _row: unknown) =>
  rendered as React.ReactNode;

function Column<TRow extends BaseRow, TValue = unknown>(props: ColumnProps<TRow, TValue>) {
  const {
    name,
    getValue,
    title,
    order,
    className,
    classes: { body: bodyClassName, head: headClassName } = {},
    visible = true,
    noControl = false,
    noSort = false,
    render = defaultRender,
    wrap = defaultWrap,
    field,
    titleNode,
    minWidth,
  } = props;

  const defaultGetValue = useCallback((row: TRow) => row[name as keyof BaseRow], [name]);

  const { addColumn, setColumns } = useContext(ColumnsContext);

  const latestGetValue = useLatest(getValue || defaultGetValue);

  const isMount = useRef(true);

  useEffect(
    () => {
      const config: ColumnConfig<TRow> = {
        name,
        title,
        titleNode,
        order,
        bodyClassName,
        headClassName,
        className,
        visible,
        noControl,
        noSort,
        field,
        getValue: latestGetValue,
        render: render as any,
        wrap: wrap as (rendered: ReactNode, value: unknown, row: TRow) => ReactNode,
        minWidth,
      };

      addColumn(config as ColumnConfig<BaseRow, unknown>, isMount.current);
      if (isMount.current) {
        isMount.current = false;
      }
    },
    // Excluded titleNode because we cant ensure it is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      addColumn,
      bodyClassName,
      className,
      field,
      headClassName,
      latestGetValue,
      minWidth,
      name,
      noControl,
      noSort,
      order,
      render,
      title,
      // titleNode,
      visible,
      wrap,
    ]
  );

  useEffect(() => {
    return () => setColumns((prev) => prev.filter((column) => column.name !== name));
    // remove the column on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default Column;
